import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { createPortal } from 'react-dom'
import confetti from 'canvas-confetti'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { orderAPI, partnerAPI } from '../services/api'
import nfcCard from '../assets/nfc_card.png'
import qrStand from '../assets/qr_stand.png'
import smartnfc from '../assets/smart_nfc_card.jpeg'
import testnfccard from '../assets/test_nfc.png'
import {
  CreditCard,
  Monitor,
  Star,
  Package,
  ArrowRight,
  Filter,
  Search,
  ShoppingBag,
  Minus,
  Plus,
  X,
  Check,
  CheckCircle,
  Shield,
  Truck,
  Zap,
  Gift
} from 'lucide-react'

const ProductsPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Helper to map product titles/IDs to backend enum types
  const getProductType = (product) => {
    const title = (product.title || product.name || '').toLowerCase();
    
    if (title.includes('subscription plan + 4 qr code stickers + 1 nfc card + 1 smart standee')) {
      return 'premium_kit';
    } else if (title.includes('subscription plan + 4 qr code stickers + 1 smart standee')) {
      return 'smart_standee';
    } else if (title.includes('subscription plan + 4 qr code stickers')) {
      return 'qr_stickers';
    } else if (title.includes('review card')) {
      return 'review_card';
    }
    
    return 'nfc_card'; // Default fallback
  };
  
  // Cart & Checkout State
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('tapon_cart')
    return saved ? JSON.parse(saved) : []
  })
  const [showCart, setShowCart] = useState(false)
  const [showCheckout, setShowCheckout] = useState(() => {
    return localStorage.getItem('tapon_show_checkout') === 'true'
  })
  const [paymentDone, setPaymentDone] = useState(() => {
    return localStorage.getItem('tapon_payment_done') === 'true'
  })
  const [showOrderSuccess, setShowOrderSuccess] = useState(false)
  const [checkout, setCheckout] = useState(() => {
    const saved = localStorage.getItem('tapon_checkout_form')
    return saved ? JSON.parse(saved) : {
      fullName: user?.name || '',
      email: user?.email || '',
      phone: '',
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    }
  })

  // Codes State (Referral & Coupon)
  const [showCodeSection, setShowCodeSection] = useState(false)
  const [hasReferral, setHasReferral] = useState(false)
  const [hasCoupon, setHasCoupon] = useState(false)

  const [enteredReferral, setEnteredReferral] = useState('')
  const [isReferralApplied, setIsReferralApplied] = useState(false)
  const [referralError, setReferralError] = useState('')

  const [enteredCoupon, setEnteredCoupon] = useState('')
  const [isCouponApplied, setIsCouponApplied] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [discountPercentage, setDiscountPercentage] = useState(0)

  // Persistence Effects
  useEffect(() => {
    localStorage.setItem('tapon_cart', JSON.stringify(cart))
  }, [cart])

  useEffect(() => {
    localStorage.setItem('tapon_show_checkout', showCheckout)
  }, [showCheckout])

  useEffect(() => {
    localStorage.setItem('tapon_payment_done', paymentDone)
  }, [paymentDone])

  useEffect(() => {
    localStorage.setItem('tapon_checkout_form', JSON.stringify(checkout))
  }, [checkout])

  // Reset codes when cart becomes empty
  useEffect(() => {
    if (cart.length === 0) {
        setDiscountPercentage(0)
        setIsCouponApplied(false)
        setEnteredCoupon('')
        setCouponError('')
        setIsReferralApplied(false)
        setEnteredReferral('')
        setReferralError('')
    }
  }, [cart])

  const triggerCelebration = () => {
    const duration = 3 * 1000
    const animationEnd = Date.now() + duration
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 }

    const randomInRange = (min, max) => Math.random() * (max - min) + min

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now()

      if (timeLeft <= 0) {
        return clearInterval(interval)
      }

      const particleCount = 50 * (timeLeft / duration)
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      })
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      })
    }, 250)
  }

  // Load Razorpay Script
  const loadScript = (src) => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Hardcoded constants for payment
  const HARD_CODED_UPI_ID = "8105183599-2@ybl" // replace with your GPay UPI
  const DISPLAY_NAME = "FiindIt"

  const categories = [
    { id: 'all', name: 'All Products', icon: Package },
   
  ]

  const products = [
    {
      id: 'qr_stickers_bundle',
      title: 'Subscription plan + 4 QR code Stickers',
      subtitle: 'Exciting offers',
      description: 'Revolutionary Smart QR code that instantly share your digital profile',
      features: [
        '1 year complete subscription', 
        'Real time Analytics dashboard',
        'Smart Digital Profile',
        '4 stickers ',
        'Multiple profile links',
        'Real-time updates'
      ],
      price: 999,
      originalPrice: 1199,
      popular: true,
      gradient: 'from-blue-500 to-purple-600',
      image: 'https://aura-print.com/media/wysiwyg/AP_Stickers_QR_Code_Stickers_Rectangle.jpg',
      category: 'nfc-cards',
      inStock: true
    },
    {
      id: 'smart_standee_bundle',
      title: 'Subscription plan + 4 QR code Stickers + 1 Smart Standee',
      subtitle: 'Exciting offers',
      description: 'Revolutionary Smart QR code that instantly share your digital profile',
      features: [
          '1 year complete subscription', //nfc card, 
        'Real time Analytics dashboard',
        'Smart Digital Profile',
        '4 stickers + 1 smart standee',
        'Multiple profile links',
        'Real-time updates'
      ],
      price: 1199,
      originalPrice: 1299,
      popular: true,
      gradient: 'from-blue-500 to-purple-600',
      image: qrStand,
      category: 'nfc-cards',
      inStock: true
    },
      {
        id: 'smart_standee_nfccard_bundle',
        title: 'Subscription plan + 4 QR code Stickers + 1 Smart Standee + 1 NFC Card',
        subtitle: 'Exciting offers',
        description: 'Revolutionary Smart QR code that instantly share your digital profile',
        features: [
            '1 year complete subscription', //nfc card, 
          'Real time Analytics dashboard',
          'Smart Digital Profile',
          '4 stickers + 1 smart standee + 1 NFC Card',
          'Multiple profile links',
          'Real-time updates',
          'AI based google review'
        ],
        price: 1499,
        originalPrice: 1799,
        popular: true,
        gradient: 'from-blue-500 to-purple-600',
        image: nfcCard,
        category: 'nfc-cards',
        inStock: true
      },
      {
        id: 'smart_nfc_card',
        title: 'Smart NFC Card',
        subtitle: 'Exciting offers',
        description: 'Revolutionary Smart NFC card that instantly share your digital profile',
        features: [
            '1 year complete subscription', //nfc card, 
          'Real time Analytics dashboard',
          'Smart Digital Profile',
          ' 1 Smart NFC Card',
          'Multiple profile links',
          'Real-time updates',
          'Customizable design'
        ],
        price: 999,
        originalPrice: 1199,
        popular: true,
        gradient: 'from-blue-500 to-purple-600',
        image: testnfccard,
        category: 'nfc-cards',
        inStock: true
      },
   
   
  ]

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory || product.id === selectedCategory
    const matchesSearch = product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  // Cart Functions
  const addToCart = (product, quantity = 1) => {
    const existingItem = cart.find(item => item.id === product.id)
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      ))
    } else {
      setCart([...cart, { ...product, name: product.title, quantity }])
    }
    toast.success(`🛒 Added ${product.title} to cart!`)
  }

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId))
    toast.success('🗑️ Removed from cart')
  }

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId)
      return
    }
    setCart(cart.map(item =>
      item.id === productId
        ? { ...item, quantity }
        : item
    ))
  }

  const handleApplyReferral = async () => {
    if (!enteredReferral) {
        setReferralError('Please enter a referral code')
        return
    }

    const code = enteredReferral.toUpperCase();

    if (code === 'FIINDITNOW') {
        setIsReferralApplied(true)
        setReferralError('')
        toast.success('Partner referral added!')
    } else {
        try {
            const res = await partnerAPI.verifyReferralCode(code);
            if (res.data.success) {
                setIsReferralApplied(true);
                setReferralError('');
                toast.success('Partner referral added!');
            } else {
                throw new Error('Invalid code');
            }
        } catch (error) {
            setIsReferralApplied(false)
            setReferralError('Invalid Referral Code')
            toast.error('Invalid Code')
        }
    }
  }

  const handleRemoveReferral = () => {
      setIsReferralApplied(false)
      setEnteredReferral('')
      setReferralError('')
      toast.success('Referral Removed')
  }

  const handleApplyCoupon = () => {
      if (!enteredCoupon) {
          setCouponError('Please enter a coupon code')
          return
      }

      const code = enteredCoupon.toUpperCase();
      if (code === 'FIINDIT20') {
          setDiscountPercentage(20)
          setIsCouponApplied(true)
          setCouponError('')
          toast.success('Coupon applied! 20% discount added.')
      } else {
          setDiscountPercentage(0)
          setIsCouponApplied(false)
          setCouponError('Invalid Coupon Code')
          toast.error('Invalid Code')
      }
  }

  const handleRemoveCoupon = () => {
      setDiscountPercentage(0)
      setIsCouponApplied(false)
      setEnteredCoupon('')
      setCouponError('')
      toast.success('Coupon Removed')
  }

  const getTotalPrice = () => {
    const total = cart.reduce((total, item) => {
        const itemPrice = Math.max(0, Math.round(item.price * (1 - discountPercentage / 100)))
        return total + (itemPrice * item.quantity)
    }, 0)
    return Math.max(0, total)
  }

  const handleCheckout = () => {
    if (cart.length === 0) return toast.error('Your cart is empty')
    
    // Removed authentication check to allow guest checkout

    setCheckout(prev => ({
        ...prev,
        fullName: user?.name || prev.fullName,
        email: user?.email || prev.email,
        phone: user?.phone || prev.phone // Also pre-fill phone if available
    }))
    setShowCheckout(true)
  }

  /*
  The user clicks "Pay Now" -> Frontend asks Backend for an ID ->
  Backend asks Razorpay -> Razorpay gives ID -> Frontend shows Payment 
  Form -> User Pays -> Razorpay gives Signature -> Frontend sends Signature 
  to Backend -> Backend confirms it's valid -> Order is Placed.
  */

  const handleRazorpayPayment = async () => {
    // Validate Contact Details First
    if (!checkout.fullName || !checkout.email || !checkout.phone || !checkout.street || !checkout.city || !checkout.state || !checkout.zipCode || !checkout.country) {
        toast.error("Please complete all shipping details");
        return;
    }

    const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

    if (!res) {
      toast.error("Razorpay SDK failed to load. Are you online?");
      return;
    }

    try {
      // 1. Create Pending Order on Backend
      const items = cart.map(i => ({
        productType: getProductType(i),
        quantity: i.quantity,
        unitPrice: Math.max(0, Math.round(i.price * (1 - discountPercentage / 100))), // Apply discount
        name: i.title || i.name,
      }));

      const payload = {
        customerInfo: {
          name: checkout.fullName,
          email: checkout.email,
          phone: checkout.phone || 'N/A'
        },
        items,
        shipping: {
          address: {
            street: checkout.street,
            city: checkout.city,
            state: checkout.state,
            zipCode: checkout.zipCode,
            country: checkout.country
          }
        },
        payment: {
            method: 'razorpay',
            status: 'pending' // Backend enforces this anyway
        },
        referralCode: isReferralApplied ? enteredReferral.toUpperCase() : null,
        notes: `Order via Products Page | Contact: ${checkout.fullName} ${checkout.phone || ''} ${isReferralApplied ? `| Referral: ${enteredReferral}` : ''} ${isCouponApplied ? `| Coupon: ${enteredCoupon} (${discountPercentage}% off)` : ''}`
      };

      console.log('Step 1: Creating Pending Order...');
      const orderRes = await orderAPI.createOrder(payload);
      console.log('Step 1 Result:', orderRes);
      
      if (!orderRes.data?.success) {
          toast.error(orderRes.data?.message || 'Failed to create order');
          return;
      }

      const orderId = orderRes.data.data._id;
      console.log('Order Created ID:', orderId);

      // 2. Create Razorpay Order linked to DB Order
      console.log('Step 2: Creating Razorpay Order...');
      const result = await orderAPI.createRazorpayOrder({
        orderId: orderId,
      });
      console.log('Step 2 Result:', result);

      if (!result.data.success) {
        toast.error("Server error initiating payment");
        return;
      }

      const { amount, id: razorpay_order_id, currency } = result.data.data;
      const { key_id } = result.data;

      const options = {
        key: key_id, 
        amount: amount.toString(),
        currency: currency,
        name: "FiindIt",
        description: "Transaction",
        order_id: razorpay_order_id,
        handler: async function (response) {
          const data = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          };

          try {
             // 3. Verify Payment
             const verifyRes = await orderAPI.verifyRazorpayPayment(data);
             if(verifyRes.data.success) {
                 // 4. Success Handling
                 setPaymentDone(true);
                 toast.success("Payment Successful!");
                 
                  // 5. Clear Cart & Cleanup
                  setCart([]);
                  setShowCheckout(false);
                  setShowCart(false);
                  setPaymentDone(false);
                  // setShowOrderSuccess(true); // Don't show modal if redirecting immediately, or show it briefly

                  localStorage.removeItem('tapon_cart');
                  localStorage.removeItem('tapon_show_checkout');
                  localStorage.removeItem('tapon_payment_done');
                  localStorage.removeItem('tapon_checkout_form');

                  // 6. Redirect to appropriate page
                 if (user) {
                     toast.success("Payment Verified! Redirecting to Dashboard...", { duration: 3000 });
                     setTimeout(() => {
                        navigate('/app/dashboard', { 
                            state: { 
                                paymentSuccess: true, 
                                orderId: razorpay_order_id, 
                                dbOrderId: orderId
                            } 
                        });
                     }, 2000);
                 } else {
                     toast.success("Payment Successful! Redirecting to setup account...", { duration: 3000 });
                     setTimeout(() => {
                        navigate('/app/register', { 
                            state: { 
                                paymentSuccess: true, 
                                orderId: razorpay_order_id, 
                                dbOrderId: orderId
                            } 
                        });
                     }, 2000);
                 }

                 triggerCelebration();
             } else {
                 toast.error("Payment verification failed");
             }
          } catch (error) {
              console.error(error);
              toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: checkout.fullName,
          email: checkout.email,
          contact: checkout.phone, 
        },
        notes: {
          address: "Razorpay Corporate Office",
        },
        theme: {
          color: "#3399cc",
        },
        modal: {
            ondismiss: function() {
                toast("Payment cancelled. Order saved as pending.", { icon: 'ℹ️' });
            }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (error) {
        console.error("Razorpay Error:", error);
        console.log("Error response:", error.response);
        toast.error(`Payment Error: ${error.response?.data?.message || error.message || "Unknown error"}`);
    }
  };

  const placeOrder = async (paymentMethod = null) => {
    // This function is now mainly for UPI / Manual orders
    // Razorpay flow is handled in handleRazorpayPayment
    try {
      if (!checkout.fullName || !checkout.email || !checkout.street || !checkout.city || !checkout.state || !checkout.zipCode || !checkout.country) {
        return toast.error('Please complete shipping details')
      }
      
      const items = cart.map(i => ({
        productType: getProductType(i), 
        quantity: i.quantity,
        unitPrice: Math.max(0, Math.round(i.price * (1 - discountPercentage / 100))), // Apply discount
        name: i.title || i.name,
      }))
      
      const payload = {
        customerInfo: {
          name: checkout.fullName,
          email: checkout.email,
          phone: checkout.phone || 'N/A'
        },
        items,
        shipping: {
          address: {
            street: checkout.street,
            city: checkout.city,
            state: checkout.state,
            zipCode: checkout.zipCode,
            country: checkout.country
          }
        },
        payment: {
            method: paymentMethod || 'upi',
            status: 'pending' 
        },
        referralCode: isReferralApplied ? enteredReferral.toUpperCase() : null,
        notes: `Order via Products Page | Contact: ${checkout.fullName} ${checkout.phone || ''} | Payment: ${paymentMethod || 'upi'} ${isReferralApplied ? `| Referral: ${enteredReferral}` : ''} ${isCouponApplied ? `| Coupon: ${enteredCoupon} (${discountPercentage}% off)` : ''}`
      }
      
      const res = await orderAPI.createOrder(payload)
      if (res.data?.success) {
        setCart([])
        setShowCheckout(false)
        setShowCart(false)
        setPaymentDone(false)
        setShowOrderSuccess(true)
        
        // Clear local storage
        localStorage.removeItem('tapon_cart')
        localStorage.removeItem('tapon_show_checkout')
        localStorage.removeItem('tapon_payment_done')
        localStorage.removeItem('tapon_checkout_form')

        triggerCelebration()
      } else {
        toast.error(res.data?.message || 'Failed to place order')
      }
    } catch (e) {
      console.error(e)
      toast.error('Failed to place order')
    }
  }

  return (
    <>
      <Helmet>
        <title>Products - FiindIt | Digital Profile Platform</title>
        <meta name="description" content="Explore our range of NFC products including business cards, smart standees, and review cards designed to transform your networking experience." />
      </Helmet>

      {/* Floating Cart Button - Fixed Position via Portal */}
      {createPortal(
        <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setShowCart(true)}
            className="fixed bottom-6 right-6 z-[9999] bg-blue-500 text-white p-4 rounded-full shadow-2xl flex items-center justify-center cursor-pointer hover:shadow-orange-500/30 transition-all duration-300 border-2 border-white/20"
            style={{ 
              boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
              position: 'fixed', // Force fixed
              bottom: '24px',    // Force bottom spacing
              right: '24px'      // Force right spacing
            }}
        >
            <ShoppingBag className="w-6 h-6" />
            {cart.length > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white animate-bounce shadow-md">
                {cart.reduce((total, item) => total + item.quantity, 0)}
            </span>
            )}
        </motion.button>,
        document.body
      )}

      <div className="pt-20 pb-16 relative">
        {/* Floating Cart Button Moved */}

        {/* Header */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Our Products
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                Choose from our range of innovative NFC products designed to transform your networking
                and lead generation experience.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${selectedCategory === category.id
                      ? 'bg-blue-500 to-accent-500 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                </motion.button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-white w-64"
              />
            </div>
          </div>
        </div>

    

        {/* Products Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProducts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                No products found
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Try adjusting your search or filter criteria.
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group"
                >
                    <div className="card p-8 h-full hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-2 flex flex-col">
                      {/* Badge */}
                      {product.badge && (
                        <div className={`absolute top-6 right-6 px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${product.gradient}`}>
                          {product.badge}
                        </div>
                      )}

                      {/* Product Image Placeholder - Replace with actual images if available */}
                       <div className={`w-full h-48 bg-gray-100 rounded-xl mb-6 flex items-center justify-center overflow-hidden`}>
                         {product.image ? (
                             <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
                         ) : (
                             <div className={`w-full h-full bg-gradient-to-br ${product.gradient} flex items-center justify-center`}>
                                <div className="text-white text-4xl font-bold">
                                {product.title.split(' ').map(word => word[0]).join('')}
                                </div>
                             </div>
                         )}
                      </div>

                      {/* Content */}
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {product.title}
                      </h3>
                      <p className="text-primary-600 dark:text-primary-400 font-medium mb-4">
                        {product.subtitle}
                      </p>
                      <p className="text-gray-600 dark:text-gray-400 mb-6 line-clamp-2">
                        {product.description}
                      </p>

                      {/* Features */}
                      <div className="grid grid-cols-2 gap-2 mb-6 flex-grow">
                        {product.features.slice(0, 4).map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                            {feature}
                          </div>
                        ))}
                      </div>

                      {/* Price and CTA */}
                      <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex flex-col">
                            <span className="text-xs text-gray-500 line-through">₹{product.originalPrice}</span>
                            <span className="text-2xl font-bold text-gray-900 dark:text-white">₹{product.price}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Link to={`/product/${product.id}`} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                                Details
                            </Link>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => addToCart(product)}
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                            >
                                <ShoppingBag className="w-4 h-4" />
                                Add
                            </motion.button>
                        </div>
                      </div>
                    </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Shopping Cart Modal via Portal */}
        {createPortal(
            <AnimatePresence>
                {showCart && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-end sm:justify-center p-0 sm:p-4 backdrop-blur-sm"
                    onClick={() => setShowCart(false)}
                >
                    <motion.div
                    initial={{ x: '100%', opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: '100%', opacity: 0 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="bg-white dark:bg-gray-800 h-full sm:h-auto sm:rounded-2xl p-6 w-full max-w-md max-h-[100vh] sm:max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                    >
                    <div className="flex items-center justify-between mb-6 flex-shrink-0">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                        <span className="text-2xl">🛒</span>
                        <span>Shopping Cart</span>
                        </h2>
                        <button
                        onClick={() => setShowCart(false)}
                        className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                        <X className="w-4 h-4" />
                        </button>
                    </div>

                    {cart.length === 0 ? (
                        <div className="text-center py-12 flex-1 flex flex-col items-center justify-center">
                        <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-6">
                            <ShoppingBag className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-900 dark:text-gray-200 mb-2">Your cart is empty</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            Looks like you haven't added anything yet.
                        </p>
                        <button 
                            onClick={() => setShowCart(false)}
                            className="mt-8 px-6 py-2 bg-primary-50 text-primary-600 rounded-lg font-medium hover:bg-primary-100 transition-colors"
                        >
                            Start Shopping
                        </button>
                        </div>
                    ) : (
                        <>
                        <div className="space-y-4 mb-6 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                            {cart.map((item) => (
                            <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-700 rounded-xl hover:bg-white dark:hover:bg-gray-700 transition-colors shadow-sm">
                                <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                                    {item.image?.startsWith('http') ? (
                                        <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className={`w-full h-full bg-gradient-to-br ${item.gradient || 'from-gray-400 to-gray-500'}`} />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                <h3 className="font-medium text-gray-900 dark:text-white truncate">
                                    {item.title || item.name}
                                </h3>
                                <p className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                                    ₹{item.price}
                                </p>
                                <div className="flex items-center space-x-3 mt-2">
                                    <div className="flex items-center bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-1">
                                        <button
                                        onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                                        className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded md:active:scale-95 transition-transform"
                                        >
                                        <Minus className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                        </button>
                                        <span className="text-sm font-semibold w-8 text-center text-gray-900 dark:text-white">
                                        {item.quantity}
                                        </span>
                                        <button
                                        onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                                        className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded md:active:scale-95 transition-transform"
                                        >
                                        <Plus className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                                        </button>
                                    </div>
                                </div>
                                </div>
                                <div className="text-right flex flex-col justify-between h-16 py-1">
                                <button
                                    onClick={() => removeFromCart(item.id)}
                                    className="text-gray-400 hover:text-red-500 transition-colors self-end p-1"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                                <p className="font-bold text-gray-900 dark:text-white">
                                    ₹{(item.price * item.quantity).toFixed(0)}
                                </p>
                                </div>
                            </div>
                            ))}
                        </div>

                        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 flex-shrink-0 bg-white dark:bg-gray-800">
                            <div className="flex items-center justify-between mb-4">
                            <span className="text-gray-500 dark:text-gray-400">Total Amount</span>
                            <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                ₹{getTotalPrice().toFixed(2)}
                            </span>
                            </div>

                            <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleCheckout}
                            className="w-full bg-green-500 text-white py-4 rounded-xl font-bold hover:from-primary-700 hover:to-accent-700 transition-all flex items-center justify-center space-x-2 shadow-lg hover:shadow-primary-500/25"
                            >
                            <CreditCard className="w-5 h-5" />
                            <span>Proceed to Checkout</span>
                            </motion.button>

                            <p className="text-xs text-center text-gray-400 mt-3 flex items-center justify-center gap-1">
                                <Shield className="w-3 h-3" /> Secure checkout powered by Razorpay
                            </p>
                        </div>
                        </>
                    )}
                    </motion.div>
                </motion.div>
                )}
            </AnimatePresence>,
            document.body
        )}

        {/* Checkout Modal via Portal */}
        {createPortal(
            <AnimatePresence>
                {showCheckout && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={() => setShowCheckout(false)}
                >
                    <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: 50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: 50 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                    >
                    <div className="flex items-center justify-between mb-6 sticky top-0 bg-white dark:bg-gray-800 z-10 pb-4 border-b border-gray-100 dark:border-gray-700">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Checkout</h3>
                        <button onClick={() => setShowCheckout(false)} className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:rotate-90 transition-transform">
                        <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Contact Information</label>
                            <div className="space-y-3">
                                <input value={checkout.fullName} onChange={e => setCheckout({ ...checkout, fullName: e.target.value })} placeholder="Full Name" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                <div className="grid grid-cols-2 gap-3">
                                    <input value={checkout.email} onChange={e => setCheckout({ ...checkout, email: e.target.value })} placeholder="Email" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                    <input value={checkout.phone} onChange={e => setCheckout({ ...checkout, phone: e.target.value })} placeholder="Phone" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                </div>
                            </div>
                        </div>
                        
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Shipping Address</label>
                            <div className="space-y-3">
                                <input value={checkout.street} onChange={e => setCheckout({ ...checkout, street: e.target.value })} placeholder="Street Address" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                <div className="grid grid-cols-2 gap-3">
                                <input value={checkout.city} onChange={e => setCheckout({ ...checkout, city: e.target.value })} placeholder="City" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                <input value={checkout.state} onChange={e => setCheckout({ ...checkout, state: e.target.value })} placeholder="State" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                <input value={checkout.zipCode} onChange={e => setCheckout({ ...checkout, zipCode: e.target.value })} placeholder="ZIP Code" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                <input value={checkout.country} onChange={e => setCheckout({ ...checkout, country: e.target.value })} placeholder="Country" className="w-full px-4 py-3 border rounded-xl bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 pt-4 border-t border-gray-100 dark:border-gray-700">
                        {/* Codes Section */}
                        <div className="mb-6">
                            <label className="flex items-center space-x-2 cursor-pointer mb-4">
                                <input 
                                    type="checkbox" 
                                    checked={showCodeSection} 
                                    onChange={(e) => setShowCodeSection(e.target.checked)}
                                    className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                                />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Have a Referral or Coupon Code?</span>
                            </label>

                            {showCodeSection && (
                                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl space-y-4 border border-gray-100 dark:border-gray-600">
                                    <div className="flex space-x-6 mb-2">
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={hasReferral}
                                                onChange={(e) => setHasReferral(e.target.checked)}
                                                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Referral Code</span>
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={hasCoupon}
                                                onChange={(e) => setHasCoupon(e.target.checked)}
                                                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Coupon Code</span>
                                        </label>
                                    </div>

                                    {hasReferral && (
                                        <div className="mt-4">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Referral Code</label>
                                            <div className="flex gap-2">
                                                <input 
                                                    value={enteredReferral} 
                                                    onChange={(e) => setEnteredReferral(e.target.value)}
                                                    disabled={isReferralApplied}
                                                    placeholder="Enter referral code" 
                                                    className="flex-1 px-4 py-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none uppercase" 
                                                />
                                                {isReferralApplied ? (
                                                    <button onClick={handleRemoveReferral} className="px-4 py-2 bg-red-100 text-red-600 rounded-xl font-medium hover:bg-red-200 transition-colors">Remove</button>
                                                ) : (
                                                    <button onClick={handleApplyReferral} className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:shadow-lg transition-all">Apply</button>
                                                )}
                                            </div>
                                            {referralError && <p className="text-red-500 text-sm mt-1">{referralError}</p>}
                                            {isReferralApplied && <p className="text-green-500 text-sm mt-1"><Gift className="w-3 h-3 inline mr-1"/>Partner referral added!</p>}
                                        </div>
                                    )}

                                    {hasCoupon && (
                                        <div className="mt-4">
                                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Coupon Code</label>
                                            <div className="flex gap-2">
                                                <input 
                                                    value={enteredCoupon} 
                                                    onChange={(e) => setEnteredCoupon(e.target.value)}
                                                    disabled={isCouponApplied}
                                                    placeholder="Enter coupon code" 
                                                    className="flex-1 px-4 py-2 border rounded-xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none uppercase" 
                                                />
                                                {isCouponApplied ? (
                                                    <button onClick={handleRemoveCoupon} className="px-4 py-2 bg-red-100 text-red-600 rounded-xl font-medium hover:bg-red-200 transition-colors">Remove</button>
                                                ) : (
                                                    <button onClick={handleApplyCoupon} className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:shadow-lg transition-all">Apply</button>
                                                )}
                                            </div>
                                            {couponError && <p className="text-red-500 text-sm mt-1">{couponError}</p>}
                                            {isCouponApplied && <p className="text-green-500 text-sm mt-1"><CheckCircle className="w-3 h-3 inline mr-1"/>Coupon applied! {discountPercentage}% off.</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Price Breakdown */}
                        <div className="space-y-2 mb-6">
                            <div className="flex items-center justify-between text-gray-500 dark:text-gray-400">
                                <span>Total Price</span>
                                <span>₹{cart.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-2">
                                <span className="font-bold text-gray-900 dark:text-white">Total Amount</span>
                                <span className="text-2xl font-bold text-gray-900 dark:text-white">₹{getTotalPrice().toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* Pay Now Button (Razorpay) */}
                            <button
                            onClick={handleRazorpayPayment}
                            disabled={paymentDone}
                            className={`w-full py-4 px-6 rounded-xl flex items-center justify-center gap-3 shadow-lg transform transition-all active:scale-[0.98]
                                ${
                                paymentDone
                                    ? "bg-green-600 shadow-green-500/20 cursor-default"
                                    : "bg-blue-500 shadow-blue-500/20 cursor-pointer hover:shadow-blue-500/40"
                                }`}
                            >
                            {paymentDone ? <Check className="w-6 h-6 text-white"/> : <CreditCard className="w-6 h-6 text-white" />}
                            <span className="text-lg font-bold text-white tracking-wide">{paymentDone ? "Payment Verified" : "Pay Now"}</span>
                            </button>
                        </div>
                    </div>

                    <button 
                        onClick={() => placeOrder('manual')} 
                        disabled={!paymentDone}
                        className="mt-6 w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 py-4 rounded-xl font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-xl transition-all active:scale-[0.98]"
                    >
                        Place Order
                    </button>
                    </motion.div>
                </motion.div>
                )}
            </AnimatePresence>,
            document.body
        )}

        {/* Order Success Celebration Modal via Portal */}
        {createPortal(
            <AnimatePresence>
            {showOrderSuccess && (
                <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
                onClick={() => setShowOrderSuccess(false)}
                >
                <motion.div
                    initial={{ scale: 0.8, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.8, opacity: 0, y: 20 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl relative overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="absolute top-0 left-0 w-full h-2 bg-green-500" />
                    
                    <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
                    className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
                    >
                    <CheckCircle className="w-10 h-10 text-green-500" />
                    </motion.div>

                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Order Placed!</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                    Thank you for your purchase. We've received your order and will process it shortly.
                    </p>

                    <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowOrderSuccess(false)}
                    className="w-full bg-orange-400 text-white py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
                    >
                    Continue Shopping
                    </motion.button>
                </motion.div>
                </motion.div>
            )}
            </AnimatePresence>,
            document.body
        )}
            
      </div>
    </>
  )
}

export default ProductsPage 